import {Injectable, NgZone} from "@angular/core";
import {io, Socket} from "socket.io-client";
import {BehaviorSubject, Observable} from "rxjs";

export type SocketConnState = "DISCONNECTED" | "CONNECTING" | "CONNECTED" | "ERROR";

@Injectable({providedIn: "root"})
export class PrintSocketService {
  private socket?: Socket;

  private readonly state$ = new BehaviorSubject<SocketConnState>("DISCONNECTED");
  private readonly url$ = new BehaviorSubject<string>("http://127.0.0.1:3000");
  private readonly error$ = new BehaviorSubject<string | null>(null);

  constructor(private zone: NgZone) {
  }

  // ====== State streams (برای UI) ======
  getState(): Observable<SocketConnState> {
    return this.state$.asObservable();
  }

  getUrl(): Observable<string> {
    return this.url$.asObservable();
  }

  getError(): Observable<string | null> {
    return this.error$.asObservable();
  }

  setUrl(url: string) {
    this.url$.next((url ?? "").trim());
  }

  // ====== Lifecycle ======
  connect(baseUrl?: string) {
    if (baseUrl) this.setUrl(baseUrl);

    // اگر وصل هست، کاری نکن
    if (this.socket?.connected) return;

    // اگر قبلاً ساخته شده، تمیز ببند و دوباره بساز
    this.disconnect();

    const url = this.url$.value;
    this.state$.next("CONNECTING");
    this.error$.next(null);

    this.socket = io(url, {
      path: "/socket.io",
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
      timeout: 8000,
    });

    const run = (fn: () => void) => this.zone.run(fn);

    this.socket.on("connect", () => run(() => this.state$.next("CONNECTED")));
    this.socket.on("disconnect", () => run(() => this.state$.next("DISCONNECTED")));

    this.socket.on("connect_error", (err: any) =>
      run(() => {
        this.state$.next("ERROR");
        this.error$.next(err?.message ?? "connect_error");
      })
    );

    // وقتی reconnect می‌خواد تلاش کنه
    this.socket.io.on("reconnect_attempt", () => run(() => this.state$.next("CONNECTING")));
  }

  disconnect() {
    if (!this.socket) {
      this.state$.next("DISCONNECTED");
      return;
    }

    try {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    } finally {
      this.socket = undefined;
      this.state$.next("DISCONNECTED");
    }
  }

  // ====== your events ======
  onQueued(): Observable<{ jobId: string }> {
    return this.fromEvent("print:queued");
  }

  onStatus(): Observable<{ jobId: string | null; status: string; message?: string }> {
    return this.fromEvent("print:status");
  }

  async sendFactorToPrinter(adminId: string, data?: any) {
    if (!this.socket || !this.socket.connected) {
      throw new Error("Socket is not connected");
    }
    this.socket.emit("print:request", {adminId, data});
  }

  private fromEvent<T>(event: string): Observable<T> {
    return new Observable<T>((sub) => {
      const handler = (data: T) => this.zone.run(() => sub.next(data));

      if (!this.socket) {
        // اگر هنوز connect نشده، فقط سابسکرایب ثبت میشه بعد از connect کار می‌کنه؟
        // بهترین کار: قبل subscribe همیشه connect بزن.
      } else {
        this.socket.on(event, handler);
      }

      // برای اینکه اگر بعداً connect شد هم listener نصب بشه:
      const attachIfReady = () => {
        if (this.socket) this.socket.on(event, handler);
      };
      attachIfReady();

      return () => {
        this.socket?.off(event, handler);
      };
    });
  }
}
